/**
 * Belge servisi — Storage upload + Firestore metadata.
 *
 * Yeni yüklemelerde Content-Disposition: attachment header set edilir,
 * böylece URL'ye gidince browser direkt indirme başlatır (yeni sekme yok).
 */

import {
  collection, doc, addDoc, getDocs, getDoc, updateDoc,
  query, where, orderBy, Timestamp,
} from 'firebase/firestore'
import {
  ref, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage'
import { getFirestoreDb, getFirebaseStorage } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp, IsoTarih } from '@/types'

const COLL = 'belgeler'

export type BelgeTipi =
  | 'kimlikOn'
  | 'kimlikArka'
  | 'pasaport'
  | 'calismaIzni'
  | 'ehliyet'
  | 'saglikRaporu'
  | 'sozlesme'
  | 'sertifika'
  | 'diger'

export const BELGE_TIPI_ETIKETLERI: Record<BelgeTipi, string> = {
  kimlikOn: 'Kimlik (Ön)',
  kimlikArka: 'Kimlik (Arka)',
  pasaport: 'Pasaport',
  calismaIzni: 'Çalışma İzni',
  ehliyet: 'Ehliyet',
  saglikRaporu: 'Sağlık Raporu',
  sozlesme: 'Sözleşme',
  sertifika: 'Sertifika',
  diger: 'Diğer',
}

export interface Belge {
  id: string
  personelId: string
  tipi: BelgeTipi
  ozelTip?: string
  dosyaUrl: string
  dosyaYolu: string
  dosyaAdi: string
  mimeType: string
  boyut: number
  gecerlilikTarihi?: IsoTarih
  yukleyenId: string
  yuklemeTarihi: FsTimestamp
  silindi: boolean
  silenId?: string
  silmeTarihi?: FsTimestamp
}

export interface BelgeYuklemeData {
  personelId: string
  tipi: BelgeTipi
  ozelTip?: string
  gecerlilikTarihi?: IsoTarih
  dosya: File
}

/**
 * Türkçe karakterler için RFC 5987 contentDisposition.
 */
function asciiSafe(name: string): string {
  return name.replace(/[^\x20-\x7E]/g, '_')
}

export async function belgeYukle(
  data: BelgeYuklemeData,
  yukleyenId: string
): Promise<Belge> {
  const db = getFirestoreDb()
  const storage = getFirebaseStorage()

  if (data.dosya.size > 10 * 1024 * 1024) {
    throw new Error('Dosya 10 MB\'dan büyük olamaz')
  }
  const okMime = ['image/jpeg', 'image/png', 'application/pdf']
  if (!okMime.includes(data.dosya.type)) {
    throw new Error('Sadece JPG, PNG veya PDF kabul edilir')
  }

  const uzanti = data.dosya.name.split('.').pop()?.toLowerCase() ?? 'bin'
  const dosyaId = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  const dosyaYolu = `belgeler/${data.personelId}/${dosyaId}.${uzanti}`

  // Storage'a yükle — Content-Disposition: attachment header'ı set
  const storageRef = ref(storage, dosyaYolu)
  const safeName = asciiSafe(data.dosya.name)
  await uploadBytes(storageRef, data.dosya, {
    contentType: data.dosya.type,
    contentDisposition: `attachment; filename="${safeName}"; filename*=UTF-8''${encodeURIComponent(data.dosya.name)}`,
  })
  const dosyaUrl = await getDownloadURL(storageRef)

  // Firestore metadata
  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    tipi: data.tipi,
    dosyaUrl,
    dosyaYolu,
    dosyaAdi: data.dosya.name,
    mimeType: data.dosya.type,
    boyut: data.dosya.size,
    yukleyenId: yukleyenId,
    yuklemeTarihi: nowServer(),
    silindi: false,
  }
  if (data.ozelTip?.trim()) docData.ozelTip = data.ozelTip.trim()
  if (data.gecerlilikTarihi) docData.gecerlilikTarihi = data.gecerlilikTarihi

  const ref2 = await addDoc(collection(db, COLL), docData)
  const snap = await getDoc(ref2)
  return { id: snap.id, ...(snap.data() as Omit<Belge, 'id'>) }
}

export async function personelBelgeleri(personelId: string): Promise<Belge[]> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, COLL),
    where('personelId', '==', personelId),
    where('silindi', '==', false),
    orderBy('yuklemeTarihi', 'desc')
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Belge, 'id'>) }))
}

export async function belgeSil(belgeId: string, silenId: string): Promise<void> {
  const db = getFirestoreDb()
  const storage = getFirebaseStorage()

  const ref2 = doc(db, COLL, belgeId)
  const snap = await getDoc(ref2)
  if (!snap.exists()) throw new Error('Belge bulunamadı')

  const belge = snap.data() as Belge

  try {
    const fileRef = ref(storage, belge.dosyaYolu)
    await deleteObject(fileRef)
  } catch (err) {
    console.warn('Storage silme hatası (devam ediliyor):', err)
  }

  await updateDoc(ref2, {
    silindi: true,
    silenId,
    silmeTarihi: Timestamp.now(),
  })
}

export async function belgeGecerlilikGuncelle(
  belgeId: string,
  gecerlilikTarihi: IsoTarih | null
): Promise<void> {
  const db = getFirestoreDb()
  const ref2 = doc(db, COLL, belgeId)
  if (gecerlilikTarihi) {
    await updateDoc(ref2, { gecerlilikTarihi })
  } else {
    await updateDoc(ref2, { gecerlilikTarihi: null })
  }
}
